/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Formik, FieldArray } from 'formik'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import { useModal } from '../../contexts/ModalContext'
import { useServiceContext } from '../../root'
import { workflows as workflowsApi } from '../../services/api'
import { Select, OptionType } from '../Select/Select'
import DatePicker from '../ui/DatePicker'
import Modal from '../Modal'
import { LoadingModal } from './LoadingModal'
import { AddCustomJustificationModal } from './AddCustomJustificationModal'
import { UserGroupSteps } from '../workflow/UserGroupSteps'
import { ErrorMessageStyled } from '../shared/ErrorMessage'
import { WF_STEP_PROCESSING_TYPE } from '../../constants/workflow-steps'
import type { DetailedWorkflow } from '../../services/api/workflows'

interface WorkflowEditModalProps {
  workflowId: number
  workflow?: any
  onSuccess?: () => void
}

interface CollaboratorsStep {
  processingType: string
  nofAllValue: string | number
  taskTeamStepLabel: { id: number; text: string } | null
  merged: any[]
  taskTeams?: any[]
}

interface FormValues {
  description: string
  justification: number | null
  justificationText: string | null
  expirationDate: Date | null
  collaborators: {
    steps: CollaboratorsStep[]
  }
}

/**
 * Transform collaborators data from server format to React format
 * Uses string format "USER-{id}" or "GROUP-{id}" for merged array
 * to match SelectUserGroup component's option value format (_uid)
 * Pattern matches DataService.tsx usersGroups format
 */
const transformFromServer = (data: any): { collaborators: { steps: CollaboratorsStep[] } } => {
  const result: { collaborators: { steps: CollaboratorsStep[] } } = {
    collaborators: {
      steps: []
    }
  }

  if (data.collaborators) {
    data.collaborators.forEach((step: any) => {
      const stepObj: CollaboratorsStep = {
        processingType: step.processingType,
        nofAllValue: step.nofAllValue || '',
        taskTeamStepLabel: step.taskTeamStepLabel,
        merged: []
      }

      if (step.taskTeams) {
        step.taskTeams.forEach((teamItem: any) => {
          // Use string format "TYPE-id" to match SelectUserGroup's _uid format
          if (teamItem.group) {
            stepObj.merged.push('GROUP-' + teamItem.group.id)
          } else if (teamItem.user) {
            stepObj.merged.push('USER-' + teamItem.user.id)
          }
        })
      }

      result.collaborators.steps.push(stepObj)
    })
  }

  return result
}

/**
 * Transform collaborators data to server format
 * Converts string format "USER-{id}" or "GROUP-{id}" back to API format
 * Pattern matches DataService.tsx transformToServer
 */
const transformToServer = (collaborators: { steps: CollaboratorsStep[] }): any[] => {
  const result: any[] = []

  collaborators.steps.forEach((step) => {
    const stepObj: any = {
      processingType: step.processingType,
      taskTeamStepLabel: step.taskTeamStepLabel ? { id: step.taskTeamStepLabel.id } : null,
      nofAllValue: step.processingType === WF_STEP_PROCESSING_TYPE.N_OF_ALL ? step.nofAllValue : null,
      taskTeams: []
    }

    step.merged.forEach((item: any) => {
      // Handle string format "TYPE-id" (from SelectUserGroup)
      if (typeof item === 'string') {
        const [type, id] = item.split('-')
        if (type === 'USER') {
          stepObj.taskTeams.push({ user: { id } })
        } else if (type === 'GROUP') {
          stepObj.taskTeams.push({ group: { id } })
        }
      } else if (item && typeof item === 'object') {
        // Handle object format for backward compatibility
        if (item.systemHelperType === 'USER') {
          stepObj.taskTeams.push({ user: { id: item.id } })
        } else {
          stepObj.taskTeams.push({ group: { id: item.id } })
        }
      }
    })

    result.push(stepObj)
  })

  return result
}

export const WorkflowEditModal: React.FC<WorkflowEditModalProps> = ({
  workflowId,
  workflow: initialWorkflow,
  onSuccess
}) => {
  const { t } = useTranslation()
  const { hideModal, showModal } = useModal()
  const queryClient = useQueryClient()
  const context = useServiceContext()

  const [loading, setLoading] = useState(true)
  const [workflow, setWorkflow] = useState<DetailedWorkflow | null>(initialWorkflow || null)
  const [justificationOptions, setJustificationOptions] = useState<Array<{ value: number; label: string }>>([])
  const [initialFormData, setInitialFormData] = useState<FormValues | null>(null)

  const maxDescriptionLength = 254

  useEffect(() => {
    loadWorkflowData()
  }, [workflowId])

  const loadWorkflowData = async () => {
    try {
      setLoading(true)

      // Load workflow details if not provided
      let wfData = initialWorkflow
      if (!wfData?.workflowType) {
        wfData = await workflowsApi.getWorkflow(workflowId)
      }

      // Build promises array for parallel loading
      const promises: Promise<any>[] = []
      promises.push(workflowsApi.getJustifications())

      // Load collaborators if canEditWorkflowCollaborators flag is true
      if (wfData.canEditWorkflowCollaborators) {
        promises.push(workflowsApi.getCREditors(wfData.id, true))
      }

      const results = await Promise.all(promises)

      // Process justifications
      const justifications = results[0]
      const justificationOpts = justifications.elements.map((j: any) => ({
        value: j.id,
        label: j.text
      }))
      justificationOpts.push({ value: 0, label: t('OPTION_CUSTOM') })
      setJustificationOptions(justificationOpts)

      // Find matching justification
      const matchingJustification = justifications.elements.find((j: any) => j.text === wfData.justificationText)

      // Determine justification ID value
      let justificationId: number | null = null
      if (wfData.justificationText) {
        justificationId = matchingJustification ? matchingJustification.id : 0
      }

      // Process collaborators data if loaded
      let collaboratorsData = { collaborators: { steps: [] as CollaboratorsStep[] } }
      if (wfData.canEditWorkflowCollaborators && results[1]) {
        collaboratorsData = transformFromServer(results[1])
      }

      // Set initial form data
      const formDataObj: FormValues = {
        description: wfData.description || '',
        justification: justificationId,
        justificationText: wfData.justificationText || null,
        expirationDate: wfData.expirationDate ? new Date(wfData.expirationDate) : null,
        ...collaboratorsData
      }

      setInitialFormData(formDataObj)
      setWorkflow(wfData)
      setLoading(false)
    } catch (error) {
      console.error('Failed to load workflow data:', error)
      setLoading(false)
    }
  }

  const handleCancel = () => {
    hideModal()
  }

  const validateForm = (values: FormValues): Record<string, any> => {
    const errors: Record<string, any> = {}

    if (!values.description?.trim()) {
      errors.description = t('FORM_VALIDATION_ERROR_REQUIRED_FIELD')
    }

    if (workflow?.flags?.canEditExpirationDate && workflow?.requireExpirationDate && !values.expirationDate) {
      errors.expirationDate = t('FORM_VALIDATION_ERROR_REQUIRED_FIELD')
    }

    // Validate collaborators if canEditWorkflowCollaborators
    if (workflow?.canEditWorkflowCollaborators) {
      if (!values.collaborators.steps || values.collaborators.steps.length === 0) {
        errors.collaborators = { steps: t('FORM_VALIDATION_ERROR_REQUIRED_FIELD') }
      } else {
        const stepsErrors: any[] = []
        values.collaborators.steps.forEach((step, i) => {
          const stepError: any = {}

          if (!step.merged || step.merged.length === 0) {
            stepError.merged = t('FORM_VALIDATION_ERROR_REQUIRED_FIELD')
          }

          if (
            step.processingType === WF_STEP_PROCESSING_TYPE.N_OF_ALL &&
            (!step.nofAllValue || +step.nofAllValue < 1)
          ) {
            stepError.nofAllValue = t('FORM_VALIDATION_ERROR_NUMBER_MUST_BE_EQ_OR_MORE_THAN_1')
          }

          if (Object.keys(stepError).length > 0) {
            stepsErrors[i] = stepError
          }
        })

        if (stepsErrors.some((e) => e !== undefined)) {
          errors.collaborators = { steps: stepsErrors }
        }
      }
    }

    return errors
  }

  const handleSubmit = async (values: FormValues) => {
    // Check if anything changed (dirty check)
    if (isEqual(initialFormData, values)) {
      handleCancel()
      return
    }

    try {
      // First update collaborators if changed
      if (
        workflow?.canEditWorkflowCollaborators &&
        initialFormData &&
        !isEqual(initialFormData.collaborators, values.collaborators)
      ) {
        const collaboratorsPayload = {
          collaborators: transformToServer(values.collaborators)
        }
        await workflowsApi.updateCREditors(workflowId, collaboratorsPayload, true)
      }

      // Then update workflow
      const updateData: any = {
        description: values.description
      }

      if (workflow?.justificationText) {
        if (values.justification === 0) {
          updateData.customJustificationText = values.justificationText
          updateData.justificationTextId = null
        } else {
          updateData.justificationTextId = values.justification
          updateData.customJustificationText = null
        }
      }

      if (
        workflow?.flags?.canEditExpirationDate &&
        values.expirationDate &&
        workflow?.expirationDate &&
        values.expirationDate.getTime() !== workflow.expirationDate
      ) {
        updateData.expirationDate = values.expirationDate.getTime()
      }

      await workflowsApi.updateWorkflow(workflowId, updateData)

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
      queryClient.invalidateQueries({ queryKey: ['workflow', workflowId] })

      // Show success toast
      context.service.toast.success(t('CHANGE_REQUEST_EDIT_SUCCESS_MSG'))

      hideModal()

      if (onSuccess) {
        onSuccess()
      }
    } catch (error: any) {
      console.error('Failed to update workflow:', error)
      context.service.toast.error(error?.data?.message || t('CHANGE_REQUEST_EDIT_ERROR_MSG'))
    }
  }

  const formatDateForDisplay = (date: Date | null): string => {
    if (!date) return ''
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleCancel()
    }
  }

  if (loading || !initialFormData) {
    return (
      <LoadingModal
        title={t('CHANGE_REQUEST_EDIT_TITLE')}
        onBackdropClick={handleBackdropClick}
        onClose={handleCancel}
      />
    )
  }

  return (
    <Modal show={true} handleClose={handleCancel} title={t('CHANGE_REQUEST_EDIT_TITLE')} size="lg">
      <Formik
        initialValues={cloneDeep(initialFormData)}
        enableReinitialize
        validate={validateForm}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, handleSubmit: formikSubmit, isSubmitting, errors, touched }) => (
          <>
            <Modal.Body>
              {/* Workflow Number */}
              <div className="form-group">
                <label className="control-label">{t('WORKFLOW_CHANGE_REQUEST_NUMBER')}</label>
                <p className="form-control-static whitespaceprewrap">{workflow?.workflowNo}</p>
              </div>

              {/* Description */}
              <div className={`form-group ${errors.description && touched.description ? 'has-error' : ''}`}>
                <label htmlFor="description" className="control-label">
                  {t('DESCRIPTION')}
                </label>
                <div style={{ width: '50%' }}>
                  <textarea
                    id="description"
                    className="form-control"
                    name="description"
                    rows={6}
                    maxLength={maxDescriptionLength}
                    value={values.description}
                    onChange={(e) => setFieldValue('description', e.target.value)}
                    disabled={isSubmitting}
                  />
                  <div
                    className="help-block"
                    style={{
                      textAlign: 'right',
                      marginTop: '5px',
                      display: 'block',
                      color: '#737373',
                      fontSize: '12px'
                    }}
                  >
                    {t('CHAR_COUNT_LIMIT')}: {maxDescriptionLength - values.description.length}
                  </div>
                  <ErrorMessageStyled name="description" />
                </div>
              </div>

              {/* Justification */}
              {workflow?.justificationText && (
                <div className="form-group">
                  <label htmlFor="justification" className="control-label">
                    {t('JUSTIFICATION')}
                  </label>
                  <Select
                    id="justification"
                    name="justification"
                    value={values.justification}
                    options={[
                      { value: '', label: t('SELECT_JUSTIFICATION_LABEL') || 'Select justification' },
                      ...justificationOptions
                    ]}
                    onChange={(selected) => {
                      if (selected && !Array.isArray(selected)) {
                        const opt = selected as OptionType
                        setFieldValue('justification', opt.value !== '' ? opt.value : null)
                      } else {
                        setFieldValue('justification', null)
                      }
                    }}
                    isClearable={false}
                    isDisabled={isSubmitting}
                  />

                  {/* Custom Justification Button */}
                  {values.justification === 0 && (
                    <button
                      type="button"
                      className="btn btn-chita btn-add-step btn-second mt10"
                      style={{ marginTop: '10px' }}
                      onClick={() => {
                        showModal(
                          <AddCustomJustificationModal
                            request={{ customJustificationText: values.justificationText }}
                            setRequest={(updater) => {
                              const updated = updater({ customJustificationText: values.justificationText })
                              setFieldValue('justificationText', updated.customJustificationText)
                            }}
                            setShowAddCustomModal={() => {}}
                          />,
                          { keepParentModalsOpen: true }
                        )
                      }}
                    >
                      {t('DESCRIPTION')}
                    </button>
                  )}
                </div>
              )}

              {/* Expiration Date */}
              {((!workflow?.flags?.canEditExpirationDate && workflow?.expirationDate) ||
                workflow?.flags?.canEditExpirationDate) && (
                <div className={`form-group ${errors.expirationDate ? 'has-error' : ''}`}>
                  <label htmlFor="expirationDate" className="control-label">
                    {t('EXPIRATION_DATE')}
                  </label>
                  {workflow?.flags?.canEditExpirationDate ? (
                    <>
                      <DatePicker
                        id="expirationDate"
                        name="expirationDate"
                        selected={values.expirationDate}
                        minDate={(() => {
                          const tomorrow = new Date()
                          tomorrow.setDate(tomorrow.getDate() + 1)
                          return tomorrow
                        })()}
                        onChange={(date) => {
                          setFieldValue('expirationDate', date)
                        }}
                        disabled={isSubmitting}
                        placeholder={t('SELECT_EXPIRATION_DATE') || 'Select expiration date'}
                      />
                      <ErrorMessageStyled name="expirationDate" />
                    </>
                  ) : (
                    <p className="form-control-static">
                      {workflow?.expirationDate ? formatDateForDisplay(new Date(workflow.expirationDate)) : ''}
                    </p>
                  )}
                </div>
              )}

              {/* Collaborators/Editors Section */}
              {workflow?.canEditWorkflowCollaborators && (
                <>
                  <FieldArray name="collaborators.steps">
                    {(arrayHelpers) => (
                      <UserGroupSteps
                        arrayHelpers={arrayHelpers}
                        values={values.collaborators.steps}
                        nofallTxt="BROWSER_FILE_SETTINGS_EDITORS_INFO_LABEL"
                        labelTxt="EDITORS"
                      />
                    )}
                  </FieldArray>
                  {(!values.collaborators.steps || values.collaborators.steps.length === 0) && (
                    <ErrorMessageStyled name="collaborators.steps" />
                  )}
                </>
              )}
            </Modal.Body>

            <Modal.Footer>
              <button type="button" className="btn btn-link" onClick={handleCancel} disabled={isSubmitting}>
                {t('CANCEL')}
              </button>
              <button type="button" className="btn btn-primary" onClick={() => formikSubmit()} disabled={isSubmitting}>
                {isSubmitting ? t('SAVING') : t('OK')}
              </button>
            </Modal.Footer>
          </>
        )}
      </Formik>
    </Modal>
  )
}

export default WorkflowEditModal
